/**
 * Multi-floor HQ programming.
 *
 * Three responsibilities:
 *   1. The authoritative Phase-4 zone/reserved-footprint contract.
 *   2. A floor-aware 32-seat production slot set: 16 desks on L1, 16 on L2,
 *      and deliberately no production desks on L0/L3.
 *   3. Stable zone roots and subtle semantic markers for the final furnishing
 *      and live projection systems. Construction grey-boxes are not rendered
 *      in the production scene.
 *
 * Coordinates are floor-LOCAL (the interior island offset is applied by the
 * collider placement + world root during setupScene). +X = EAST = bay glass.
 */
import * as pc from 'playcanvas'
import type { AssetZoneController } from '../assetZones'
import { DESK_OFFSETS, meetingLightOn } from './layout'
import { worldRoot } from './worldRoot'
import { registerCollider } from '../colliders'
import { createRenderEntity } from './interiorShared'
import { buildHqFurnishing, HQ_FURNITURE } from './hqFurnishing'
import {
  buildHqSummaryBoards,
  type HqProgramSummary,
} from './hqSummaryBoards'
import {
  createLightweightFigure,
  createLightweightFigureMaterials,
  lightweightMotionOffset,
  type LightweightFigure,
} from './lightweightFigure'
import { CHARACTER_GROUND_CLEARANCE_M, phaseOffsetSeconds } from '../characters'
import { buildHqL1ProductionFinish } from './hqProductionPodFinish'

/**
 * Z offset of the interior coordinate island (matches interiors.ts
 * INTERIOR_ISLAND_Z). Duplicated as a plain const so this module — imported by
 * the unit-tested stations/scene modules — doesn't pull in the heavy
 * interiors → library → labels chain (which needs the full PC runtime).
 */
const INTERIOR_ISLAND_Z = -1000

/** Per-floor walk-surface Y — matches scene.ts FLOOR_Y. */
export const FLOOR_Y = [0, 4.5, 9, 13.5] as const

/**
 * Desk-cluster centers are the Phase-4 production program, not a generic
 * overflow grid. Every pod faces +X toward the bay.
 */
interface FloorPlan {
  y: number
  centers: ReadonlyArray<readonly [number, number]>
}
export const L1_DESK_POD_CENTERS = [
  [-15, -9],
  [-9, -9],
  [-15, 9],
  [-9, 9],
] as const
export const L2_DESK_POD_CENTERS = [
  // The expanded far-west collaboration band removes the old 0.25 m desk-to-
  // stair pinch. Four pods keep 16 seats with at least 4.3 m to the stair.
  [-21, -3.5],
  [-15, -3.5],
  [-21, 3.5],
  [-15, 3.5],
] as const
const FLOOR_PLANS: readonly FloorPlan[] = [
  { y: FLOOR_Y[1], centers: L1_DESK_POD_CENTERS },
  { y: FLOOR_Y[2], centers: L2_DESK_POD_CENTERS },
]

interface FloorSlot {
  x: number
  y: number
  z: number
  rotY: number
}
const SLOTS: readonly FloorSlot[] = FLOOR_PLANS.flatMap((plan) =>
  plan.centers.flatMap(([cx, cz]) =>
    DESK_OFFSETS.map((off) => ({ x: cx + off.dx, y: plan.y, z: cz + off.dz, rotY: off.rotY })),
  ),
)

/** Total slots across every floor (djb2 hash modulus for StationLayout). */
export const FLOOR_SLOT_COUNT = SLOTS.length

function at(slot: number): FloorSlot {
  return SLOTS[((slot % SLOTS.length) + SLOTS.length) % SLOTS.length]
}

/** Slot → floor-local desk position [x, y, z]. */
export function floorSlotPosition(slot: number): [number, number, number] {
  const s = at(slot)
  return [s.x, s.y, s.z]
}

/** Slot → desk world-Y rotation (degrees). */
export function floorSlotRotation(slot: number): number {
  void at(slot).rotY
  return 90
}

/** Slot → authoritative desk-pod zone id for behavior-owned station parenting. */
export function floorSlotZoneId(slot: number): string {
  const normalized = ((slot % FLOOR_SLOT_COUNT) + FLOOR_SLOT_COUNT) % FLOOR_SLOT_COUNT
  if (normalized < L1_DESK_POD_CENTERS.length * DESK_OFFSETS.length) {
    return `l1-desk-pod-${Math.floor(normalized / DESK_OFFSETS.length) + 1}`
  }
  return `l2-desk-pod-${
    Math.floor(
      (normalized - L1_DESK_POD_CENTERS.length * DESK_OFFSETS.length)
      / DESK_OFFSETS.length,
    ) + 1
  }`
}

// ── Authoritative floor-program contract ───────────────────────────────────

export type HqFloor = 0 | 1 | 2 | 3
export type HqZoneKind =
  | 'reception'
  | 'server'
  | 'core-observatory'
  | 'desk-pod'
  | 'focus-room'
  | 'huddle-room'
  | 'gallery'
  | 'boardroom'
  | 'kitchen'
  | 'lounge'
  | 'sky-bar'
  | 'executive'
  | 'core-crown'

export interface HqZoneBounds {
  minX: number
  maxX: number
  minZ: number
  maxZ: number
}

export interface HqProgramZone {
  id: string
  label: string
  floor: HqFloor
  kind: HqZoneKind
  bounds: HqZoneBounds
  access: readonly [number, number]
  routeNode: string
  capacity?: number
  facesBay?: true
}

function deskPodZones(
  floor: 1 | 2,
  centers: ReadonlyArray<readonly [number, number]>,
): HqProgramZone[] {
  return centers.map(([x, z], index) => ({
    id: `l${floor}-desk-pod-${index + 1}`,
    label: `L${floor} desk pod ${index + 1}`,
    floor,
    kind: 'desk-pod',
    bounds: { minX: x - 2, maxX: x + 2, minZ: z - 2, maxZ: z + 2 },
    // Approach through the 1.2 m center aisle between the two desk columns.
    // L1 enters from the void ring; L2's reconciled west-band pods enter from
    // their outer north/south edges. The former west-side L2 approach cut
    // across the actual desk/chair footprints.
    access: floor === 1
      ? [x, z < 0 ? z + 1.4 : z - 1.4]
      : [x, z < 0 ? z - 2.4 : z + 2.4],
    routeNode: floor === 2 && x < -13
      ? (z < 0 ? 'north-west' : 'south-west')
      : (z < 0 ? 'north' : 'south'),
    capacity: 4,
    facesBay: true,
  }))
}

/**
 * One source of truth for every Phase-4 zone. Bounds are reserved footprints;
 * Phase 5 may furnish inside them but must not move or silently overlap them.
 */
export const HQ_PROGRAM_ZONES: readonly HqProgramZone[] = [
  {
    id: 'l0-reception',
    label: 'Reception',
    floor: 0,
    kind: 'reception',
    bounds: { minX: -7, maxX: 7, minZ: 10, maxZ: 15 },
    access: [0, 9.6],
    routeNode: 'south',
  },
  {
    id: 'l0-server-room',
    label: 'Server room',
    floor: 0,
    kind: 'server',
    bounds: { minX: -24, maxX: -18, minZ: -14, maxZ: -10 },
    // The real L0 server door is X [-15.1,-13.9] on Z=-7. Use its center;
    // the former X=-13.4 target faced the solid south-east wall segment.
    access: [-20.5, -9.6],
    routeNode: 'north-west',
  },
  {
    id: 'l0-core-observatory',
    label: 'Core observatory',
    floor: 0,
    kind: 'core-observatory',
    bounds: { minX: -3, maxX: 1, minZ: -4, maxZ: 4 },
    access: [-3.4, 4.4],
    routeNode: 'west',
  },
  {
    id: 'l0-bay-lounge',
    label: 'Bay lounge 1',
    floor: 0,
    kind: 'lounge',
    bounds: { minX: 17, maxX: 24, minZ: -7, maxZ: 7 },
    access: [17.1, 0],
    routeNode: 'east',
    facesBay: true,
  },
  ...deskPodZones(1, L1_DESK_POD_CENTERS),
  {
    id: 'l1-focus-studio',
    label: 'Focus studio',
    floor: 1,
    kind: 'focus-room',
    bounds: { minX: -24, maxX: -18, minZ: -6, maxZ: -0.25 },
    access: [-17.6, -3],
    routeNode: 'focus-approach',
  },
  {
    id: 'l1-team-huddle',
    label: 'Team huddle room',
    floor: 1,
    kind: 'huddle-room',
    bounds: { minX: -24, maxX: -18, minZ: 0.25, maxZ: 6 },
    access: [-17.6, 3],
    routeNode: 'huddle-approach',
  },
  {
    id: 'l1-gallery',
    label: 'Operations gallery',
    floor: 1,
    kind: 'gallery',
    bounds: { minX: -23.5, maxX: -4, minZ: -14.5, maxZ: -13.6 },
    access: [-14, -13.2],
    routeNode: 'north-west',
  },
  {
    id: 'l1-bay-lounge',
    label: 'Bay lounge 2',
    floor: 1,
    kind: 'lounge',
    bounds: { minX: 17, maxX: 24, minZ: -6, maxZ: 6 },
    access: [17.1, 0],
    routeNode: 'east',
    facesBay: true,
  },
  ...deskPodZones(2, L2_DESK_POD_CENTERS),
  {
    id: 'l2-boardroom',
    label: 'Boardroom',
    floor: 2,
    kind: 'boardroom',
    // Runs between the Z=-12 and Z=-6 grid lines, putting the X=6/12
    // structural columns in the north wall instead of through the table.
    bounds: { minX: 2, maxX: 17, minZ: -12, maxZ: -6 },
    access: [1.6, -9],
    routeNode: 'north',
  },
  {
    id: 'l2-kitchen',
    label: 'Kitchen and break',
    floor: 2,
    kind: 'kitchen',
    bounds: { minX: 17, maxX: 24, minZ: 7, maxZ: 14 },
    access: [17.4, 10.5],
    routeNode: 'south-east',
  },
  {
    id: 'l2-gallery',
    label: 'Gallery mezzanine',
    floor: 2,
    kind: 'gallery',
    // Keep the mezzanine on the supported west slab, not over the open shaft.
    bounds: { minX: -4.3, maxX: -3.2, minZ: 1, maxZ: 8.5 },
    access: [-4.7, 3.2],
    routeNode: 'south',
  },
  {
    id: 'l2-bay-lounge',
    label: 'Bay lounge 3',
    floor: 2,
    kind: 'lounge',
    bounds: { minX: 17, maxX: 24, minZ: -5, maxZ: 5 },
    access: [17.1, 0],
    routeNode: 'east',
    facesBay: true,
  },
  {
    id: 'l3-penthouse-lounge',
    label: 'Penthouse bay lounge',
    floor: 3,
    kind: 'lounge',
    bounds: { minX: 17, maxX: 24, minZ: -7, maxZ: 7 },
    access: [17.1, 0],
    routeNode: 'east',
    facesBay: true,
  },
  {
    id: 'l3-sky-bar',
    label: 'Sky bar',
    floor: 3,
    kind: 'sky-bar',
    bounds: { minX: 0, maxX: 9, minZ: -14, maxZ: -8 },
    access: [-0.4, -10.5],
    routeNode: 'north',
  },
  {
    id: 'l3-executive-nook',
    label: 'Executive project room',
    floor: 3,
    kind: 'executive',
    // One complete 6 m structural bay. The former off-grid room put the
    // (-12,-12) tower column directly through its conference table.
    bounds: { minX: -18, maxX: -12, minZ: -14, maxZ: -8 },
    access: [-15, -7.6],
    routeNode: 'north-west',
  },
  {
    id: 'l3-core-crown',
    label: 'Core crown',
    floor: 3,
    kind: 'core-crown',
    // The crown is a contemplation program beside the shaft, not a platform
    // across it. Keep the full 14x12 m atrium void open through L3 and use the
    // supported south floor band beneath the oculus instead (+Z is south).
    bounds: { minX: 0.5, maxX: 7.5, minZ: 6.5, maxZ: 11.5 },
    access: [0.1, 9],
    routeNode: 'south',
  },
]

export function programZonesForFloor(floor: HqFloor): HqProgramZone[] {
  return HQ_PROGRAM_ZONES.filter((zone) => zone.floor === floor)
}

export interface HqCirculationNode {
  id: string
  x: number
  z: number
}

export interface HqCirculationFloor {
  floor: HqFloor
  nodes: readonly HqCirculationNode[]
  edges: ReadonlyArray<readonly [string, string]>
}

const RING_NODES: readonly HqCirculationNode[] = [
  { id: 'arrival', x: -5.2, z: -6.8 },
  { id: 'north', x: -3.8, z: -6.8 },
  { id: 'north-west', x: -20, z: -6.8 },
  { id: 'north-east', x: 11.8, z: -6.8 },
  { id: 'east', x: 11.8, z: 0 },
  { id: 'south-east', x: 11.8, z: 6.8 },
  { id: 'south', x: -3.8, z: 6.8 },
  { id: 'south-west', x: -20, z: 6.8 },
  { id: 'west', x: -4.2, z: 0 },
]
const RING_EDGES: ReadonlyArray<readonly [string, string]> = [
  ['arrival', 'north'],
  ['north', 'north-west'],
  ['north', 'north-east'],
  ['north-east', 'east'],
  ['east', 'south-east'],
  ['south-east', 'south'],
  ['south', 'south-west'],
  ['south', 'west'],
  ['west', 'arrival'],
]

/**
 * Explicit graph used by tests and future wayfinding. Each zone is linked to
 * the circulation ring around the void; no route crosses the open shaft.
 */
export const HQ_CIRCULATION: readonly HqCirculationFloor[] = FLOOR_Y.map((_, floor) => {
  const zones = programZonesForFloor(floor as HqFloor)
  const roomApproaches: readonly HqCirculationNode[] = floor === 1
    ? [
        { id: 'focus-approach', x: -17.35, z: -6.8 },
        { id: 'huddle-approach', x: -17.35, z: 6.8 },
      ]
    : []
  const roomApproachEdges: ReadonlyArray<readonly [string, string]> = floor === 1
    ? [
        ['north-west', 'focus-approach'],
        ['south-west', 'huddle-approach'],
      ]
    : []
  return {
    floor: floor as HqFloor,
    nodes: [
      ...RING_NODES,
      ...roomApproaches,
      ...zones.map((zone) => ({ id: zone.id, x: zone.access[0], z: zone.access[1] })),
    ],
    edges: [
      ...RING_EDGES,
      ...roomApproachEdges,
      ...zones.map((zone) => [zone.routeNode, zone.id] as const),
    ],
  }
})

// ── Static grey-boxes and partial furnishing ───────────────────────────────

function standardMaterial(
  diffuse: readonly [number, number, number],
  opacity = 1,
): pc.StandardMaterial {
  const value = new pc.StandardMaterial()
  value.diffuse = new pc.Color(...diffuse)
  value.gloss = 0.1
  if (opacity < 1) {
    value.opacity = opacity
    value.blendType = pc.BLEND_NORMAL
    value.depthWrite = false
  }
  value.update()
  return value
}

export interface HqZoneThresholdSpec {
  position: readonly [number, number, number]
  scale: readonly [number, number, number]
}

/**
 * A flush directional threshold replaces the old circular floor token. Its
 * long edge follows the nearest program boundary, so it reads as architecture
 * and wayfinding while preserving the authoritative access coordinate.
 */
export function hqZoneThresholdSpec(zone: HqProgramZone): HqZoneThresholdSpec {
  const xEdgeDistance = Math.min(
    Math.abs(zone.access[0] - zone.bounds.minX),
    Math.abs(zone.access[0] - zone.bounds.maxX),
  )
  const zEdgeDistance = Math.min(
    Math.abs(zone.access[1] - zone.bounds.minZ),
    Math.abs(zone.access[1] - zone.bounds.maxZ),
  )
  const nearestEdgeRunsAlongX = zEdgeDistance <= xEdgeDistance
  return {
    position: [
      zone.access[0],
      FLOOR_Y[zone.floor] + 0.008,
      zone.access[1],
    ],
    scale: nearestEdgeRunsAlongX
      ? [0.72, 0.016, 0.12]
      : [0.12, 0.016, 0.72],
  }
}

function buildProgramAnchors(
  app: pc.Application,
  root: pc.Entity,
): {
  zoneRoots: ReadonlyMap<string, pc.Entity>
  materials: Readonly<Record<HqZoneKind, pc.StandardMaterial>>
  batchGroupId?: number
} {
  const zoneRoots = new Map<string, pc.Entity>()
  const batchGroupId = app.batcher
    ?.addGroup('hq-program-geometry', false, 128)
    .id
  const mats: Record<HqZoneKind, pc.StandardMaterial> = {
    reception: standardMaterial([0.49, 0.36, 0.25]),
    server: standardMaterial([0.12, 0.18, 0.22]),
    'core-observatory': standardMaterial([0.10, 0.28, 0.34]),
    'desk-pod': standardMaterial([0.45, 0.39, 0.29]),
    'focus-room': standardMaterial([0.25, 0.35, 0.38]),
    'huddle-room': standardMaterial([0.36, 0.31, 0.38]),
    gallery: standardMaterial([0.29, 0.34, 0.40]),
    boardroom: standardMaterial([0.27, 0.35, 0.40]),
    kitchen: standardMaterial([0.51, 0.47, 0.39]),
    lounge: standardMaterial([0.20, 0.39, 0.37]),
    'sky-bar': standardMaterial([0.36, 0.28, 0.22]),
    executive: standardMaterial([0.31, 0.27, 0.24]),
    'core-crown': standardMaterial([0.12, 0.42, 0.31]),
  }

  for (const zone of HQ_PROGRAM_ZONES) {
    const parent = new pc.Entity(`hq-zone-${zone.id}`)
    root.addChild(parent)
    zoneRoots.set(zone.id, parent)
    // Phase 4 used full-zone coloured pads and four visible desk reservation
    // boxes. The final threshold keeps the projection material seam but avoids
    // the residual circular "dropped token" language on the finished floor.
    const threshold = hqZoneThresholdSpec(zone)
    const marker = createRenderEntity({
      parent,
      name: `hq-zone-threshold-${zone.id}`,
      type: 'box',
      position: threshold.position,
      scale: threshold.scale,
      material: mats[zone.kind],
    })
    if (marker.render) {
      marker.render.castShadows = false
      if (batchGroupId !== undefined) marker.render.batchGroupId = batchGroupId
    }
  }
  return { zoneRoots, materials: mats, batchGroupId }
}

export type HqGlassDoorEdge = 'north' | 'south' | 'west' | 'east'

export interface HqGlassRoomSpec {
  id: string
  zoneId: string
  floor: HqFloor
  bounds: HqZoneBounds
  doorEdge: HqGlassDoorEdge
  /** X coordinate on north/south walls; Z coordinate on west/east walls. */
  doorCenter: number
  doorWidth: number
}

export interface HqGlassPartitionSegment extends HqZoneBounds {
  id: string
  roomId: string
  floor: HqFloor
}

/**
 * Full-height rooms replace the old pair of chest-high screen fragments.
 * Both rooms use the same declarative perimeter builder so door gaps, glass,
 * frames, and colliders cannot drift apart.
 */
export const HQ_GLASS_ROOMS: readonly HqGlassRoomSpec[] = [
  {
    id: 'focus-studio',
    zoneId: 'l1-focus-studio',
    floor: 1,
    bounds: { minX: -24, maxX: -18, minZ: -6, maxZ: -0.25 },
    doorEdge: 'east',
    doorCenter: -3,
    doorWidth: 1.8,
  },
  {
    id: 'team-huddle',
    zoneId: 'l1-team-huddle',
    floor: 1,
    bounds: { minX: -24, maxX: -18, minZ: 0.25, maxZ: 6 },
    doorEdge: 'east',
    doorCenter: 3,
    doorWidth: 1.8,
  },
  {
    id: 'boardroom',
    zoneId: 'l2-boardroom',
    floor: 2,
    bounds: { minX: 2, maxX: 17, minZ: -12, maxZ: -6 },
    doorEdge: 'west',
    doorCenter: -9,
    doorWidth: 1.8,
  },
  {
    id: 'executive-office',
    zoneId: 'l3-executive-nook',
    floor: 3,
    // Align the enclosure with the structural bay so the columns become the
    // room's corner posts rather than obstacles inside it.
    bounds: { minX: -18, maxX: -12, minZ: -14, maxZ: -8 },
    doorEdge: 'south',
    doorCenter: -15,
    doorWidth: 1.8,
  },
]

const GLASS_PARTITION_THICKNESS = 0.1

/** Axis-aligned perimeter segments with the selected doorway omitted. */
export function hqGlassRoomSegments(
  room: HqGlassRoomSpec,
): HqGlassPartitionSegment[] {
  const t = GLASS_PARTITION_THICKNESS / 2
  const { minX, maxX, minZ, maxZ } = room.bounds
  const start = room.doorCenter - room.doorWidth / 2
  const end = room.doorCenter + room.doorWidth / 2
  const segments: HqGlassPartitionSegment[] = []
  const add = (id: string, bounds: HqZoneBounds): void => {
    segments.push({ id: `${room.id}-${id}`, roomId: room.id, floor: room.floor, ...bounds })
  }

  if (room.doorEdge === 'north') {
    add('north-a', { minX, maxX: start, minZ: minZ - t, maxZ: minZ + t })
    add('north-b', { minX: end, maxX, minZ: minZ - t, maxZ: minZ + t })
  } else {
    add('north', { minX, maxX, minZ: minZ - t, maxZ: minZ + t })
  }
  if (room.doorEdge === 'south') {
    add('south-a', { minX, maxX: start, minZ: maxZ - t, maxZ: maxZ + t })
    add('south-b', { minX: end, maxX, minZ: maxZ - t, maxZ: maxZ + t })
  } else {
    add('south', { minX, maxX, minZ: maxZ - t, maxZ: maxZ + t })
  }
  if (room.doorEdge === 'west') {
    add('west-a', { minX: minX - t, maxX: minX + t, minZ, maxZ: start })
    add('west-b', { minX: minX - t, maxX: minX + t, minZ: end, maxZ })
  } else {
    add('west', { minX: minX - t, maxX: minX + t, minZ, maxZ })
  }
  if (room.doorEdge === 'east') {
    add('east-a', { minX: maxX - t, maxX: maxX + t, minZ, maxZ: start })
    add('east-b', { minX: maxX - t, maxX: maxX + t, minZ: end, maxZ })
  } else {
    add('east', { minX: maxX - t, maxX: maxX + t, minZ, maxZ })
  }
  return segments
}

export const HQ_GLASS_PARTITIONS: readonly HqGlassPartitionSegment[] =
  HQ_GLASS_ROOMS.flatMap(hqGlassRoomSegments)

/** L1 room fronts whose visible glazing is supplied by the licensed Office kit. */
export const HQ_SYNTY_GLASS_FRONT_ROOM_IDS: ReadonlySet<string> = new Set([
  'focus-studio',
  'team-huddle',
])

export function usesSyntyGlassFront(segment: HqGlassPartitionSegment): boolean {
  return HQ_SYNTY_GLASS_FRONT_ROOM_IDS.has(segment.roomId)
    && (segment.id.endsWith('-east-a') || segment.id.endsWith('-east-b'))
}

function createGlassRoomMaterials(): {
  clear: pc.StandardMaterial
  frost: pc.StandardMaterial
  frame: pc.StandardMaterial
} {
  const clear = standardMaterial([0.52, 0.76, 0.82], 0.18)
  clear.gloss = 0.95
  clear.cull = pc.CULLFACE_NONE
  clear.update()
  const frost = standardMaterial([0.66, 0.82, 0.84], 0.46)
  frost.gloss = 0.72
  frost.cull = pc.CULLFACE_NONE
  frost.update()
  const frame = standardMaterial([0.15, 0.13, 0.11])
  frame.useMetalness = true
  frame.metalness = 0.72
  frame.gloss = 0.62
  frame.update()
  return { clear, frost, frame }
}

/** One framed pane: frosted privacy band below clear vision glass. */
function buildGlassPartition(
  parent: pc.Entity,
  segment: HqGlassPartitionSegment,
  materials: ReturnType<typeof createGlassRoomMaterials>,
  batchGroupId?: number,
): void {
  const y = FLOOR_Y[segment.floor]
  const height = 3.5
  const frostHeight = 0.9
  const rail = 0.08
  const width = segment.maxX - segment.minX
  const depth = segment.maxZ - segment.minZ
  const centerX = (segment.minX + segment.maxX) / 2
  const centerZ = (segment.minZ + segment.maxZ) / 2
  const add = (
    suffix: string,
    position: readonly [number, number, number],
    scale: readonly [number, number, number],
    material: pc.StandardMaterial,
  ): void => {
    const entity = createRenderEntity({
      parent,
      name: `hq-glass-${segment.id}-${suffix}`,
      type: 'box',
      position,
      scale,
      material,
    })
    if (entity.render) {
      entity.render.castShadows = false
      if (batchGroupId !== undefined) entity.render.batchGroupId = batchGroupId
    }
  }

  // The licensed L1 entrance module owns these two visible front segments.
  // Retain the collider segments below so its centred doorway remains exactly
  // aligned with the authoritative circulation gap.
  if (!usesSyntyGlassFront(segment)) {
    add('frost', [centerX, y + frostHeight / 2, centerZ], [width, frostHeight, depth], materials.frost)
    add(
      'clear',
      [centerX, y + frostHeight + (height - frostHeight) / 2, centerZ],
      [width, height - frostHeight, depth],
      materials.clear,
    )
    add('head', [centerX, y + height - rail / 2, centerZ], [width, rail, depth], materials.frame)

    const alongX = width > depth
    const postWidth = alongX ? rail : width
    const postDepth = alongX ? depth : rail
    const endpoints: readonly [number, number][] = alongX
      ? [[segment.minX + rail / 2, centerZ], [segment.maxX - rail / 2, centerZ]]
      : [[centerX, segment.minZ + rail / 2], [centerX, segment.maxZ - rail / 2]]
    endpoints.forEach(([x, z], index) => add(
      `post-${index + 1}`,
      [x, y + height / 2, z],
      [postWidth, height, postDepth],
      materials.frame,
    ))
  }
  registerCollider({
    minX: segment.minX,
    maxX: segment.maxX,
    minY: y,
    maxY: y + height,
    minZ: segment.minZ,
    maxZ: segment.maxZ,
    blocksSight: false,
  })
}

/**
 * Furnish the upper floors. L1 pairs the live-agent desk neighborhoods with
 * two enclosed west-bay rooms and an east focus lounge. L2/L3 add larger
 * collaboration / executive rooms plus their own bay lounge.
 */
export interface HqProgramHandles {
  /** P7: illuminate collaboration/gallery zones from the shared live projection. */
  projectActivity(activeRuns: number, recentCompletions: number): void
  /** P7: authoritative recent completions occupy the L2 cooldown kitchen. */
  projectCooldown(recentCompletionIds: readonly string[]): void
  /** P7: update both live summary boards from the same shared projection. */
  projectSummary(summary: HqProgramSummary): boolean
  furnitureEntityCount: number
  furnitureZoneCount: number
  destroy(): void
}

export const HQ_COOLDOWN_FIGURE_CAP = 6
export const HQ_COOLDOWN_SEATS: readonly [number, number, number, number][] = [
  [21.35, FLOOR_Y[2], 8.4, 90],
  [21.35, FLOOR_Y[2], 9.2, 90],
  [21.35, FLOOR_Y[2], 10.0, 90],
  [23.6, FLOOR_Y[2], 8.4, 270],
  [23.6, FLOOR_Y[2], 9.2, 270],
  [23.6, FLOOR_Y[2], 10.0, 270],
]

export function cooldownOccupantIds(
  recentCompletionIds: readonly string[],
): string[] {
  return [...new Set(recentCompletionIds.filter(
    (id): id is string => typeof id === 'string' && id.length > 0 && id.length <= 256,
  ))].slice(0, HQ_COOLDOWN_FIGURE_CAP)
}

export function programActivityView(
  activeRuns: number,
  recentCompletions: number,
): {
  boardroomActive: boolean
  galleryIntensity: number
} {
  const safeCompletions = Number.isFinite(recentCompletions)
    ? Math.max(0, Math.floor(recentCompletions))
    : 0
  return {
    boardroomActive: meetingLightOn(
      Number.isFinite(activeRuns) ? Math.max(0, Math.floor(activeRuns)) : 0,
    ),
    galleryIntensity: safeCompletions > 0
      ? Math.min(1.2, 0.35 + safeCompletions * 0.08)
      : 0,
  }
}

export function buildFloorPrograms(
  app: pc.Application,
  assetZones?: AssetZoneController<pc.Asset>,
): HqProgramHandles {
  const root = worldRoot(app)
  const { zoneRoots, materials, batchGroupId } =
    buildProgramAnchors(app, root)
  buildHqL1ProductionFinish(root, batchGroupId)
  const glassMaterials = createGlassRoomMaterials()
  for (const segment of HQ_GLASS_PARTITIONS) {
    buildGlassPartition(root, segment, glassMaterials, batchGroupId)
  }

  // L3 — observation only: penthouse lounge, sky bar, exec nook, Core crown.
  const furnishing = buildHqFurnishing(app, zoneRoots, assetZones)
  const summaryBoards = buildHqSummaryBoards(app, root)
  const cooldownBatchGroupId = app.batcher
    ?.addGroup('hq-kitchen-cooldown', true, 20)
    .id
  const cooldownMaterials = createLightweightFigureMaterials(
    new pc.Color(0.18, 0.38, 0.42),
    new pc.Color(0.74, 0.55, 0.38),
  )
  const cooldownFigures: LightweightFigure[] = HQ_COOLDOWN_SEATS.map(
    ([x, y, z, yaw], index) => {
      const figure = createLightweightFigure(
        zoneRoots.get('l2-kitchen') ?? root,
        cooldownBatchGroupId,
        cooldownMaterials,
        `hq-kitchen-cooldown-${index + 1}`,
      )
      figure.root.setLocalPosition(x, y, z)
      figure.root.setLocalEulerAngles(0, yaw, 0)
      // The procedural figure has no seated skeleton. Keep it standing and
      // grounded beside the communal table instead of burying its feet to fake
      // a chair pose.
      figure.model.setLocalPosition(0, CHARACTER_GROUND_CLEARANCE_M, 0)
      figure.root.enabled = false
      return figure
    },
  )
  let cooldownElapsed = 0
  const animateCooldown = (dt: number): void => {
    cooldownElapsed += dt
    for (let index = 0; index < cooldownFigures.length; index++) {
      if (!cooldownFigures[index].root.enabled) continue
      const phase = phaseOffsetSeconds(`hq-cooldown-${index}`, 3.6)
      cooldownFigures[index].model.setLocalPosition(
        0,
        CHARACTER_GROUND_CLEARANCE_M + lightweightMotionOffset(
          cooldownElapsed,
          phase,
          'idle',
          false,
        ),
        0,
      )
    }
  }
  const appEvents = app as unknown as {
    on?: (event: string, callback: (dt: number) => void) => void
    off?: (event: string, callback: (dt: number) => void) => void
  }
  appEvents.on?.('update', animateCooldown)

  return {
    furnitureEntityCount: furnishing.entities.length,
    furnitureZoneCount: new Set(HQ_FURNITURE.map((item) => item.zoneId)).size,
    projectActivity(activeRuns, recentCompletions): void {
      const view = programActivityView(activeRuns, recentCompletions)
      const boardroomActive = view.boardroomActive
      materials.boardroom.emissive = boardroomActive
        ? new pc.Color(0.18, 0.65, 0.73)
        : new pc.Color(0, 0, 0)
      materials.boardroom.emissiveIntensity = boardroomActive ? 0.85 : 0
      materials.boardroom.update()

      const galleryActive = view.galleryIntensity > 0
      materials.gallery.emissive = galleryActive
        ? new pc.Color(0.13, 0.52, 0.62)
        : new pc.Color(0, 0, 0)
      materials.gallery.emissiveIntensity = view.galleryIntensity
      materials.gallery.update()
    },
    projectCooldown(recentCompletionIds): void {
      const occupants = cooldownOccupantIds(recentCompletionIds)
      for (let index = 0; index < cooldownFigures.length; index++) {
        cooldownFigures[index].root.enabled = index < occupants.length
      }
    },
    projectSummary(summary): boolean {
      return summaryBoards.project(summary)
    },
    destroy(): void {
      appEvents.off?.('update', animateCooldown)
      for (const figure of cooldownFigures) figure.root.destroy()
      summaryBoards.destroy()
    },
  }
}

// ── Projected roster placement ──────────────────────────────────────────────

export interface RosterPlacement {
  x: number
  y: number
  z: number
  rotY: number
  room: string
}

export const ROSTER_CAPACITY = SLOTS.length

/** Deterministic desk placement for a project-scoped catalog agent. */
export function rosterPlacement(index: number): RosterPlacement {
  if (!Number.isInteger(index) || index < 0 || index >= ROSTER_CAPACITY) {
    throw new RangeError(`roster placement ${index} exceeds capacity ${ROSTER_CAPACITY}`)
  }
  const slot = SLOTS[index]
  const floor = FLOOR_Y.indexOf(slot.y as (typeof FLOOR_Y)[number])
  return {
    ...slot,
    // Match the live-run pods: every production desk faces the bay (+X).
    rotY: floorSlotRotation(index),
    room: `L${floor} · Team`,
  }
}

/** Convert an interior-local placement into the world position used by labels. */
export function rosterWorldPosition(
  placement: RosterPlacement,
): [number, number, number] {
  return [placement.x, placement.y, placement.z + INTERIOR_ISLAND_Z]
}
